import { useState, useEffect } from "react";
import toast from "react-hot-toast";
import useCreate from "./useCreate";
import useRead from "./useRead";

const useWalletSettings = () => {
  const [loading, setLoading] = useState(false);

  // Initialize useRead hook for fetching wallet settings
  const {
    data: walletSettingsData,
    paramsObject,
    setGetListParams,
  } = useRead({
    url: "fetch_wallet_settings",
    initialData: {},
    method: "GET",
    enabled: true,
    onSuccess: (data) => {
      // Update local state when data is fetched successfully
      if (data && data.b2b) {
        setB2bConfig({
          ...data.b2b,
          status: data.b2b.status || "ENABLED",
        });
        setWalletSettings((prev) => ({
          ...prev,
          enableB2B: data.b2b.status === "ENABLED",
        }));
      }
      if (data && data.b2c) {
        setB2cConfig({
          ...data.b2c,
          status: data.b2c.status || "ENABLED",
        });
        setWalletSettings((prev) => ({
          ...prev,
          enableB2C: data.b2c.status === "ENABLED",
        }));
      }
    },
  });

  // Initialize useCreate hook for saving wallet settings
  const { setDataToServer } = useCreate({
    url: "manage_wallet_settings",
    refreshUrl: "fetch_wallet_settings",
    onSuccess: () => {
      toast.success("Wallet settings saved successfully!", {
        position: "top-right",
        duration: 3000,
      });
      // Refetch data after successful save
      setGetListParams({});
    },
  });

  // State for wallet settings
  const [walletSettings, setWalletSettings] = useState({
    enableB2B: false,
    enableB2C: false,
  });

  // State for B2B configuration
  const [b2bConfig, setB2bConfig] = useState({
    threshold: "",
    min_order_value: "",
    max_order_value: "",
    redeem_value: "", // Percentage (e.g., 5%)
    status: "DISABLED", // ENABLED | DISABLED
  });

  // State for B2C configuration
  const [b2cConfig, setB2cConfig] = useState({
    threshold: "",
    min_order_value: "",
    max_order_value: "",
    redeem_value: "", // Percentage (e.g., 3%)
    status: "DISABLED", // ENABLED | DISABLED
  });

  // Load existing settings from API is now handled by useRead hook

  const handleToggle = (settingType) => {
    setWalletSettings((prev) => ({
      ...prev,
      [settingType]: !prev[settingType],
    }));

    // Update status in the respective config objects
    if (settingType === "enableB2B") {
      setB2bConfig((prev) => ({
        ...prev,
        status: !walletSettings.enableB2B ? "ENABLED" : "DISABLED",
      }));
    }

    if (settingType === "enableB2C") {
      setB2cConfig((prev) => ({
        ...prev,
        status: !walletSettings.enableB2C ? "ENABLED" : "DISABLED",
      }));
    }
  };

  const handleB2BConfigChange = (field, value) => {
    // Store the raw value for empty strings, otherwise parse as float
    let processedValue = value === "" ? "" : parseFloat(value) || 0;

    // For redeem_value, ensure it's within valid percentage range (0-100) when not empty
    if (field === "redeem_value" && value !== "") {
      processedValue = Math.max(0, Math.min(100, processedValue));
    }

    setB2bConfig((prev) => ({
      ...prev,
      [field]: processedValue,
    }));
  };

  const handleB2CConfigChange = (field, value) => {
    // Store the raw value for empty strings, otherwise parse as float
    let processedValue = value === "" ? "" : parseFloat(value) || 0;

    // For redeem_value, ensure it's within valid percentage range (0-100) when not empty
    if (field === "redeem_value" && value !== "") {
      processedValue = Math.max(0, Math.min(100, processedValue));
    }

    setB2cConfig((prev) => ({
      ...prev,
      [field]: processedValue,
    }));
  };

  const handleSave = () => {
    const data = {
      b2b: { ...b2bConfig },
      b2c: { ...b2cConfig },
    };
    console.log("Saving wallet settings:", data);

    setDataToServer.mutate(data);
  };

  return {
    // State
    loading,
    saving: setDataToServer.isLoading,
    walletSettings,
    b2bConfig,
    b2cConfig,
    // API Data
    walletSettingsData,
    paramsObject,
    setGetListParams,
    // Handlers
    handleToggle,
    handleB2BConfigChange,
    handleB2CConfigChange,
    handleSave,
  };
};

export default useWalletSettings;
